import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { message, Table } from "antd";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";

import ColumnSettingsDrawer from "./ColumnSettingsDrawer";
import Pagination from "../../Pagination/Pagination";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getConfigurableDataTableStorageKey,
  MIN_CONFIGURABLE_COLUMN_WIDTH,
  readColumnSettings,
  reconcileColumnSettings,
  writeColumnSettings,
} from "./configurableDataTablePersistence";
import styles from "./ConfigurableDataTable.module.css";

const settingsAreEqual = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

const DEFAULT_COLUMN_WIDTH = 120;

function ResizableHeaderCell({
  width,
  minWidth = MIN_CONFIGURABLE_COLUMN_WIDTH,
  onResize,
  onResizeStop,
  headerCellComponent: HeaderCellComponent = "th",
  children,
  ...restProps
}) {
  if (!width) {
    return (
      <HeaderCellComponent {...restProps}>{children}</HeaderCellComponent>
    );
  }

  return (
    <Resizable
      width={width}
      height={0}
      axis="x"
      minConstraints={[minWidth, 0]}
      handle={
        <span
          className={styles.resizeHandle}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        />
      }
      onResize={onResize}
      onResizeStop={onResizeStop}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <HeaderCellComponent {...restProps}>{children}</HeaderCellComponent>
    </Resizable>
  );
}

function ConfigurableDataTable({
  id,
  columns = [],
  settingsOpen = false,
  onSettingsClose,
  dataSource,
  data,
  className,
  paginationConfig,
  pagination: tablePagination,
  scroll,
  fillHeight = false,
  fillHeightOffset = 88,
  fillHeightMin = 240,
  components,
  ...tableProps
}) {
  const tableAreaRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(null);
  const catalog = useMemo(
    () =>
      columns.map((column, index) => {
        const key = column.key ?? column.dataIndex ?? `column-${index}`;
        const title = column.columnSettingsLabel ?? column.title;
        return {
          key,
          label:
            typeof title === "string" || typeof title === "number"
              ? String(title)
              : String(key),
        };
      }),
    [columns],
  );
  const columnKeys = useMemo(
    () => catalog.map((column) => column.key),
    [catalog],
  );
  const columnMinimumWidths = useMemo(
    () =>
      Object.fromEntries(
        columns.flatMap((column, index) => {
          const minimum = Number(column.minWidth);
          if (!Number.isFinite(minimum)) return [];
          const key = column.key ?? column.dataIndex ?? `column-${index}`;
          return [[key, Math.max(1, minimum)]];
        }),
      ),
    [columns],
  );
  const storageKey = useMemo(
    () => getConfigurableDataTableStorageKey(id),
    [id],
  );
  const [settings, setSettings] = useState(() =>
    readColumnSettings(storageKey, columnKeys, columnMinimumWidths),
  );
  const previousStorageKeyRef = useRef(storageKey);

  useEffect(() => {
    setSettings((current) => {
      if (previousStorageKeyRef.current !== storageKey) {
        previousStorageKeyRef.current = storageKey;
        return readColumnSettings(
          storageKey,
          columnKeys,
          columnMinimumWidths,
        );
      }
      const reconciled = reconcileColumnSettings(
        columnKeys,
        current,
        columnMinimumWidths,
      );
      return settingsAreEqual(current, reconciled) ? current : reconciled;
    });
  }, [columnKeys, columnMinimumWidths, storageKey]);

  const resizeColumn = useCallback(
    (key, width, persist, minWidth) => {
      const normalizedWidth = Math.max(
        minWidth,
        Math.round(width),
      );
      setSettings((current) => {
        const nextSettings = {
          ...current,
          widths: {
            ...current.widths,
            [key]: normalizedWidth,
          },
        };
        if (persist) writeColumnSettings(storageKey, nextSettings);
        return nextSettings;
      });
    },
    [storageKey],
  );

  const visibleColumns = useMemo(() => {
    const columnsByKey = new Map(
      columns.map((column, index) => [
        column.key ?? column.dataIndex ?? `column-${index}`,
        column,
      ]),
    );
    const visibleKeys = new Set(settings.visibleKeys);

    return settings.order
      .filter((key) => visibleKeys.has(key))
      .map((key) => {
        const column = columnsByKey.get(key);
        if (!column) return null;
        const configuredWidth = Number(settings.widths?.[key]);
        const defaultWidth = Number(column.width);
        const configuredMinWidth = Number(column.minWidth);
        const minWidth = Number.isFinite(configuredMinWidth)
          ? Math.max(1, configuredMinWidth)
          : MIN_CONFIGURABLE_COLUMN_WIDTH;
        const preferredWidth = Number.isFinite(configuredWidth)
          ? configuredWidth
          : Number.isFinite(defaultWidth)
            ? defaultWidth
            : DEFAULT_COLUMN_WIDTH;
        const width = Math.max(minWidth, preferredWidth);

        return {
          ...column,
          width,
          onHeaderCell: (headerColumn) => ({
            ...(typeof column.onHeaderCell === "function"
              ? column.onHeaderCell(headerColumn)
              : {}),
            width,
            minWidth,
            onResize: (_event, { size }) =>
              resizeColumn(key, size.width, false, minWidth),
            onResizeStop: (_event, { size }) =>
              resizeColumn(key, size.width, true, minWidth),
            headerCellComponent: components?.header?.cell,
          }),
        };
      })
      .filter(Boolean);
  }, [columns, components?.header?.cell, resizeColumn, settings]);

  const saveSettings = (nextSettings) => {
    const reconciled = reconcileColumnSettings(
      columnKeys,
      nextSettings,
      columnMinimumWidths,
    );
    const settingsChanged = !settingsAreEqual(settings, reconciled);
    setSettings(reconciled);
    writeColumnSettings(storageKey, reconciled);
    if (settingsChanged) {
      message.success(AppStrings.PAM_CONFIGURABLE_TABLE_SETTINGS_UPDATED);
    }
    onSettingsClose?.();
  };

  const resolvedDataSource = dataSource ?? data ?? [];

  useLayoutEffect(() => {
    if (!fillHeight || typeof window === "undefined") {
      setMeasuredHeight(null);
      return undefined;
    }

    let frame = null;
    const measure = () => {
      frame = null;
      const container = tableAreaRef.current;
      if (!container) return;
      const body =
        container.querySelector(".ant-table-body") ||
        container.querySelector(".ant-table-container");
      if (!body) return;

      const availableHeight = Math.max(
        fillHeightMin,
        Math.round(
          window.innerHeight -
            body.getBoundingClientRect().top -
            fillHeightOffset,
        ),
      );
      setMeasuredHeight((current) =>
        current === availableHeight ? current : availableHeight,
      );
    };
    const scheduleMeasure = () => {
      if (frame == null) {
        frame = window.requestAnimationFrame(measure);
      }
    };

    measure();
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [
    fillHeight,
    fillHeightMin,
    fillHeightOffset,
    resolvedDataSource.length,
  ]);

  const resolvedScroll =
    fillHeight && measuredHeight != null
      ? { ...scroll, y: measuredHeight }
      : scroll;
  const mergedComponents = useMemo(
    () => ({
      ...components,
      header: {
        ...components?.header,
        cell: ResizableHeaderCell,
      },
    }),
    [components],
  );

  return (
    <>
      <ColumnSettingsDrawer
        open={settingsOpen}
        onClose={onSettingsClose}
        columns={catalog}
        settings={settings}
        onSave={saveSettings}
      />
      <div ref={tableAreaRef} className={styles.tableArea}>
        <Table
          {...tableProps}
          id={id}
          className={[styles.configurableTable, className]
            .filter(Boolean)
            .join(" ")}
          columns={visibleColumns}
          components={mergedComponents}
          dataSource={resolvedDataSource}
          pagination={paginationConfig ? false : tablePagination}
          scroll={resolvedScroll}
        />
        {paginationConfig ? <Pagination {...paginationConfig} /> : null}
      </div>
    </>
  );
}

export default memo(ConfigurableDataTable);
